// Play-side network adapter over the shell's `src/lib/api.ts` and `src/lib/ws.ts`: typed JSON calls that raise
// `ApiFailure` (with `network` set when the answer was lost, so the same requestId may be retried), and
// `subscribeRoom(code, cb)` that merges room views, chat, reactions and transport state into one stream.
import type { RoomView, WsServerMessage } from "@digu/shared";
import { api as shellApi, ApiError } from "../../lib/api";
import { onChat, onNotify, onReaction, onSocketState, socketState, subscribeRoom as shellSubscribeRoom } from "../../lib/ws";

export { newRequestId } from "../../lib/api";

export class ApiFailure extends Error {
  readonly status: number;
  readonly code: string | undefined;
  /** True when the request never reached the server or no response came back (safe to retry with the same requestId). */
  readonly network: boolean;
  constructor(message: string, status: number, code: string | undefined, network: boolean) {
    super(message);
    this.name = "ApiFailure";
    this.status = status;
    this.code = code;
    this.network = network;
  }
}

export async function api<T>(method: "GET" | "POST", path: string, body?: unknown): Promise<T> {
  try {
    return method === "GET" ? await shellApi.get<T>(path) : await shellApi.post<T>(path, body ?? {});
  } catch (e) {
    if (e instanceof ApiError) throw new ApiFailure(e.message, e.status, e.code, e.uncertain || e.status >= 500);
    throw new ApiFailure("The table didn't answer. Your cards and seat are kept.", 0, undefined, true);
  }
}

export type RoomStreamMessage = WsServerMessage | { t: "transport"; mode: "live" | "polling" | "offline" } | { t: "removed"; watching?: boolean; ended?: boolean };
export type RoomStreamHandler = (msg: RoomStreamMessage) => void;

/**
 * Subscribe to a room: `room` views (socket push, or the 2 s poll while the socket is down), `chat` and `reaction`
 * events for this room, and `transport` changes (`live` on the socket, `polling` on HTTP, `offline` when the poll
 * itself fails).
 */
export function subscribeRoom(code: string, cb: RoomStreamHandler): () => void {
  let mode: "live" | "polling" | "offline" = socketState() === "open" ? "live" : "polling";
  const setMode = (m: typeof mode) => { if (mode !== m) { mode = m; cb({ t: "transport", mode: m }); } };
  const offRoom = shellSubscribeRoom(code, (view: RoomView) => { cb({ t: "room", view }); if (mode === "offline") setMode(socketState() === "open" ? "live" : "polling"); }, {
    onError: (e) => {
      if (e instanceof ApiError && e.uncertain) setMode("offline");
      else if (e instanceof ApiError && e.status === 403 && e.code === "removed") cb({ t: "removed" });
      else if (e instanceof ApiError && e.status === 403 && e.code === "watch-removed") cb({ t: "removed", watching: true });
      else if (e instanceof ApiError && e.status === 403 && e.code === "watch-ended") cb({ t: "removed", watching: true, ended: true });
    },
  });
  // The host removed this player from the waiting room (D-079): the server says so once over the socket.
  // D-120: a watcher the host removed hears it the same way, marked `watching`; D-121: `ended` when their friend left.
  const offNotify = onNotify((m) => {
    const p = m.payload as { roomCode?: string; removed?: boolean; watching?: boolean; ended?: boolean } | null;
    if (m.kind === "info" && p?.removed && p.roomCode === code) cb(p.watching ? { t: "removed", watching: true, ...(p.ended ? { ended: true } : {}) } : { t: "removed" });
  });
  const offState = onSocketState((s) => { if (s === "open") setMode("live"); else if (mode === "live") setMode("polling"); });
  const offChat = onChat((roomCode, message) => { if (roomCode === code) cb({ t: "chat", roomCode, message }); });
  const offReaction = onReaction((roomCode, reaction) => { if (roomCode === code) cb({ t: "reaction", roomCode, reaction }); });
  cb({ t: "transport", mode });
  return () => { offRoom(); offState(); offChat(); offReaction(); offNotify(); };
}
